import {useCustomQuery} from "~/query/queryClient";
import {queryHttp} from "~/query/queryHttp";
import {useStores} from "~/stores";
import {DISPLAY_URL} from "../services/categoryProfileUrls";

/** What customers see of the Category Profile (staff settings, Display group). */
export type CategoryProfileDisplay = {
    showDashboardCard: boolean;
    hiddenTabs: string[];
    /** Mithra staff keep seeing hidden parts, marked as hidden. */
    seesHidden: boolean;
};

/** The Display settings; any signed-in user reads them. Only asked for when the customer has the Category Profile. */
export function useCategoryProfileDisplay(enabled = true) {
    return useCustomQuery({
        queryKey: [DISPLAY_URL] as const,
        queryFn: async () => (await queryHttp.get<CategoryProfileDisplay>(DISPLAY_URL)).data,
        enabled,
        retry: false,
        staleTime: 5 * 60 * 1000,
    });
}

/** The tab every profile always shows: it cannot be hidden. */
const ALWAYS_SHOWN_TAB = 'overview';

/**
 * Whether a profile tab is hidden for this user (Display settings): parts of other tabs that lead to it hide too.
 * Fails closed: until the settings load (or if they cannot be loaded) every tab but Overview counts as hidden.
 */
export function useTabHidden(): (tab: string) => boolean {
    const {data} = useCategoryProfileDisplay();
    return tab => tab !== ALWAYS_SHOWN_TAB && (!data || (!data.seesHidden && data.hiddenTabs.includes(tab)));
}

/**
 * Whether the Category Profile card (dashboard, menu, Spend Landscape bar) shows: the customer configuration makes it
 * available (`showCards`), the Display settings show it to customers. Staff always see it. Fails closed: hidden until
 * the settings are loaded, and if they cannot be loaded.
 */
export function useCategoryProfileCardVisible(): boolean {
    const {p, authStore} = useStores();
    const available = p.showCard('category-explore', authStore);
    const display = useCategoryProfileDisplay(available);
    if (!available) return false;
    return Boolean(display.data && (display.data.showDashboardCard || display.data.seesHidden));
}
